/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Utils for Input Screen of Eligibility
 */
import _ from 'lodash';

import dateParser from 'lib/dateParser';
import {
	fieldsConfigMapping,
	providerIdentifierKeys,
	requiredFieldsMapping,
	schemaKeys,
} from 'components/forms/schema/eligibilityRequestFormSchema';
import { groupTransactionMaintenance } from 'utils/admin/payers';
import { mapPatientDataToFormFields } from 'utils/eligibility/patientFieldMapper';
import {
	DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE,
	ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP,
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES,
	ELIGIBILITY_PAYER_STATUS_CODES,
	TIME_UNITS,
} from 'app-constants/eligibility';
import { MONTH_DATE_YEAR_SLASH_FORMAT, YEAR_MONTH_DATE_FORMAT } from '@veritable/common/constants';
import { TRANSACTION_TYPE_CODES } from 'app-constants/admin/payer';
import { ISchemaValues } from 'hooks/eligibility/useEligibilityFormSchema';

const ACTIVE_PAYERS_CODES = [ELIGIBILITY_PAYER_STATUS_CODES.active];

const PAYER_ENABLED_CODES = [ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.completed, ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.notApplicable];

const ENROLLMENT_REQUIRED_CODES = [
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.notEnrolled,
	ELIGIBILITY_PAYER_ENROLLMENT_STATUS_CODES.requested,
];

const ADD_FORM_TYPE: EligibilityRequestForm.IFormType = { add: true };
const CLAIM_FORM_TYPE: EligibilityRequestForm.IFormType = { claim: true };
const EDIT_FORM_TYPE: EligibilityRequestForm.IFormType = { edit: true };
const COVERAGE_FORM_TYPE: EligibilityRequestForm.IFormType = { coverage: true };
const PATIENT_FORM_TYPE: EligibilityRequestForm.IFormType = { patient: true };

const defaultServiceType = 'Health Benefit Plan Coverage';

/**
 * function to convert input text to uppercase
 */
const convertToUpperCase = (text: string): string => _.trim(_.toUpper(text));

/**
 * function to disable future dates for datepicker
 */
const disableFutureDates = (currentDate: IDate): boolean => {
	return currentDate >= dateParser().endOf('day');
};

/**
 * function to get first selected combinations
 */
const getFirstSelectedCombination = (allCombination: string[][], selectedKeys: string[]): string => {
	const selectedCombinations = _.compact(
		_.map(allCombination, (combination) => {
			if (_.isEmpty(_.difference(combination, selectedKeys))) {
				return JSON.stringify(combination);
			}
		}),
	);
	return _.first(selectedCombinations) ?? '';
};

/**
 * function to get selected patient combination
 */
const getSelectedPatientCombination = (allCombination?: string[][], model?: EligibilityResponse.IModel): string => {
	if (_.isEmpty(model) || !allCombination) {
		return '';
	}
	const patientDetails = model?.requestInfo?.patientInfo;
	const patientKeys = {
		[requiredFieldsMapping.memberId]: patientDetails?.memberId,
		[requiredFieldsMapping.ssn]: patientDetails?.ssn,
		[requiredFieldsMapping.medicaidId]: patientDetails?.medicaidId,
		[requiredFieldsMapping.groupNumber]: patientDetails?.groupNumber,
		[requiredFieldsMapping.firstName]: patientDetails?.name?.firstname,
		[requiredFieldsMapping.lastName]: patientDetails?.name?.lastname,
		[requiredFieldsMapping.dateOfBirth]: patientDetails?.dob,
		[requiredFieldsMapping.gender]: patientDetails?.gender,
		[requiredFieldsMapping.state]: patientDetails?.address?.state,
	};
	const selectedKeys = _.keys(_.pickBy(patientKeys, _.identity));
	return getFirstSelectedCombination(allCombination, selectedKeys);
};

/**
 * function to get selected provider identifier combination
 */
const getSelectedProviderCombination = (
	allCombination?: string[][],
	model?: EligibilityRequestForm.IProviderIdentifierPatchModel,
): string => {
	if (_.isEmpty(model) || !allCombination) {
		return '';
	}
	// all the key names from model are same as required fields mapping for provider identifiers
	const selectedKeys = _.concat(_.keys(_.pickBy(model, _.identity)), [requiredFieldsMapping.npi]);
	return getFirstSelectedCombination(allCombination, selectedKeys);
};

// initial model for provider identifiers
const prepareProviderIdentifierInitialModel = (
	model?: EligibilityRequestForm.IProviderIdentifierStatusModel,
): EligibilityRequestForm.IProviderIdentifierKeys => {
	const providerIdentifierModel = {} as EligibilityRequestForm.IProviderIdentifierKeys;
	_.forEach(providerIdentifierKeys, (key) => {
		providerIdentifierModel[key] = _.get(model, key)?.value ?? '';
	});
	return providerIdentifierModel;
};

// get provider identifiers by provider npi
const getProviderIdentifiersById = (
	id: string,
	providerPayerMeta?: Payers.IProviderPayerMeta[],
): EligibilityRequestForm.IProviderIdentifierStatusModel | undefined => {
	return _.find(providerPayerMeta, {
		providerId: id,
	})?.identifierStatus;
};

/**
 * function to prepare initial model for eligibility form
 */
const prepareEligibilityFormInitialModel = ({
	formType,
	payerID,
	providerNPI,
	serviceType,
	requiredFieldsCombination,
	location,
	model,
	claimModel,
	coverageEligibilityModel,
	providerPayerData,
	patientData,
	patientCoverages,
}: {
	formType: EligibilityRequestForm.IFormType;
	payerID: string;
	providerNPI: string;
	requiredFieldsCombination: EligibilityRequestForm.IRequiredFieldsGrouping;
	location?: string;
	model?: EligibilityResponse.IModel;
	claimModel?: ClaimStatusResponse.IModel;
	coverageEligibilityModel?: Insurance.CoverageEligibilityRequestInfo;
	serviceType: string[];
	providerPayerData?: Payers.IPayerMeta;
	patientData?: Patients.IDetailedResponse;
	patientCoverages?: Insurance.ICoverageResponseModel[];
}): EligibilityRequestForm.IFormModel => {
	const { edit: isEdit, claim: isClaim, coverage: isCoverage, patient: isPatient } = formType;
	const currentDate = dateParser().startOf('day').format(YEAR_MONTH_DATE_FORMAT);
	const providerIdentifiers = getProviderIdentifiersById(providerNPI, providerPayerData?.providerPayerMeta?.data);
	const eligibilityRequestInfo = model?.requestInfo;
	const claimRequestInfo = claimModel?.claimStatusRequestResponse?.requestInfo;
	const coveragePatientInfo = coverageEligibilityModel?.patientInfo;
	const matchingCoverage = _.find(patientCoverages, { id: payerID });

	return {
		npi: providerNPI || '',
		locationId: location || null,
		payerId: payerID || '',
		relation:
			(isEdit
				? eligibilityRequestInfo?.patientInfo?.relationship
				: isClaim
					? claimRequestInfo?.patientInfo?.relationship
					: isCoverage
						? coveragePatientInfo?.relationship
						: ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP) || ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP,
		memberId:
			(isEdit
				? eligibilityRequestInfo?.patientInfo?.memberId
				: isClaim
					? claimRequestInfo?.patientInfo?.memberId
					: isCoverage
						? coveragePatientInfo?.memberId
						: '') || '',
		ssn: (isEdit ? eligibilityRequestInfo?.patientInfo?.ssn : isCoverage ? coveragePatientInfo?.ssn : '') || '',
		medicaidId: (isEdit ? eligibilityRequestInfo?.patientInfo?.medicaidId : isCoverage ? coveragePatientInfo?.medicaidId : '') || '',
		groupNumber: (isEdit ? eligibilityRequestInfo?.patientInfo?.groupNumber : isCoverage ? coveragePatientInfo?.groupNumber : '') || '',
		state:
			(isEdit ? eligibilityRequestInfo?.patientInfo?.address?.state : isCoverage ? coveragePatientInfo?.address?.state : null) ||
			null,
		firstName:
			(isEdit
				? eligibilityRequestInfo?.patientInfo?.name?.firstname
				: isClaim
					? claimRequestInfo?.patientInfo?.name?.firstname
					: isCoverage
						? coveragePatientInfo?.name?.firstname
						: '') || '',
		lastName:
			(isEdit
				? eligibilityRequestInfo?.patientInfo?.name?.lastname
				: isClaim
					? claimRequestInfo?.patientInfo?.name?.lastname
					: isCoverage
						? coveragePatientInfo?.name?.lastname
						: '') || '',
		dateOfBirth:
			(isEdit
				? eligibilityRequestInfo?.patientInfo?.dob
				: isClaim
					? claimRequestInfo?.patientInfo?.dob
					: isCoverage
						? coveragePatientInfo?.dob
						: '') || '',
		gender:
			(isEdit
				? eligibilityRequestInfo?.patientInfo?.gender
				: isClaim
					? claimRequestInfo?.patientInfo?.gender
					: isCoverage
						? coveragePatientInfo?.gender
						: null) || null,
		serviceStartDate:
			(isEdit ? eligibilityRequestInfo?.asOfDate : isClaim ? claimRequestInfo?.beginningDateOfService : currentDate) || currentDate,
		serviceEndDate: (isEdit ? eligibilityRequestInfo?.toDate : isClaim ? claimRequestInfo?.endDateOfService : '') || '',
		serviceType:
			(isEdit
				? eligibilityRequestInfo?.serviceType
				: !_.isEmpty(serviceType)
					? serviceType
					: [DEFAULT_ELIGIBILITY_SERVICE_TYPE_CODE]) || [],
		patientReqFields: requiredFieldsCombination?.patient,
		providerReqFields: requiredFieldsCombination?.provider,
		...prepareProviderIdentifierInitialModel(providerIdentifiers),
		...((isPatient && mapPatientDataToFormFields(patientData, matchingCoverage)) || {}),
	};
};

/**
 * function to prepare submit model for eligibility form
 */
const prepareEligibilitySubmitModel = (
	model: EligibilityRequestForm.IFormModel,
	formDetails: ISchemaValues,
	formType: EligibilityRequestForm.IFormType,
): EligibilityRequestForm.ISubmitModel => {
	const patientInfo: EligibilityRequestForm.IPatient = {
		name: {
			firstname: model.firstName,
			lastname: model.lastName,
		},
		contact: [],
	};
	if (model.relation) {
		patientInfo.relationship = model.relation;
	}
	if (model.memberId) {
		patientInfo.memberId = model.memberId;
	}
	if (model.ssn) {
		patientInfo.ssn = model.ssn;
	}
	if (model.medicaidId) {
		patientInfo.medicaidId = model.medicaidId;
	}
	if (model.groupNumber) {
		patientInfo.groupNumber = model.groupNumber;
	}
	if (model.dateOfBirth) {
		patientInfo.dob = model.dateOfBirth;
	}

	if (model.gender) {
		patientInfo.gender = model.gender;
	}
	if (model.state) {
		patientInfo.address = {
			...patientInfo?.address,
			state: model.state,
		};
	}

	// prepare provider identifiers to send to api
	const updatedProviderIdentifiers = _.pick(model, _.keys(providerIdentifierKeys));
	const providerIdentifiers = _.pickBy(
		updatedProviderIdentifiers,
		(value, key) => !!value && value !== _.get(formDetails?.initialModel, key),
	);

	const item: EligibilityRequestForm.ISubmitModel = {
		patientInfo,
		providerId: model.npi,
		tenantpayerId: model.payerId,
		asOfDate: model.serviceStartDate,
		serviceType: model.serviceType,
		procedureCode: [],
	};

	if (model.locationId) {
		item.locationId = model.locationId;
	}

	if (model.serviceEndDate) {
		item.toDate = model.serviceEndDate;
	}
	if (!_.isEmpty(providerIdentifiers)) {
		item.providerIdentifier = providerIdentifiers;
	}

	// This is required as in patientForm, payerId stores coverageId, so need to explicitly override
	// We can remove if (formType.patient), but keeping it instead to have a better understanding,
	// so that we dont remove it mistakenly
	if (formType.patient) {
		item.tenantpayerId = formDetails.payerId;
	}
	return item;
};
/**
 * function to prepare resubmit model for eligibility network error
 */
const prepareEligibilityResubmitModel = (data: EligibilityResponse.IModel): EligibilityRequestForm.ISubmitModel => data?.requestInfo;

const isValidPersistCombination = (
	requiredFieldsCombination: EligibilityRequestForm.IOptionProps[],
	persistedCombination: string,
): boolean => {
	const requiredFieldsValues = _.map(requiredFieldsCombination, 'value');
	return _.includes(requiredFieldsValues, persistedCombination);
};

// Helper function to check if NPI is valid
const isValidProvider = (providerID?: string, payerData?: Payers.IPayerMeta, metadata?: Metadata.IResponse): boolean => {
	if (!providerID || !payerData || !metadata) {
		return false;
	}
	return !!_.find(payerData?.providerPayerMeta?.data, ({ providerId, enrollmentStatus }) => {
		const providerMetadata = _.get(metadata?.providerMetaData, [providerId]);
		return providerID === providerId && _.includes(PAYER_ENABLED_CODES, enrollmentStatus) && !!providerMetadata?.canMakeRequests;
	});
};

// get initial payer id for add form
const getInitialPayer = (selectedActiveEligibilityPayers: Record<string, Payers.ISelectedPayer>): string => {
	const enrollmentNotRequiredPayer = _.find(
		_.values(selectedActiveEligibilityPayers),
		({ isEnrollmentRequiredForEligibility }) => isEnrollmentRequiredForEligibility === false,
	)?.id;
	if (enrollmentNotRequiredPayer) {
		return enrollmentNotRequiredPayer;
	}
	return (
		_.find(
			_.values(selectedActiveEligibilityPayers),
			({ providerEnrollmentMeta }) => _.isNumber(providerEnrollmentMeta?.done) && providerEnrollmentMeta?.done > 0,
		)?.id || ''
	);
};

const calculateDatesFromConfig = (config: Payers.IFieldConfig): EligibilityRequestForm.IPastFutureDate => {
	let pastDate;
	let futureDate;
	const today = dateParser().startOf('day');

	if (config?.past) {
		const validTimeUnit = _.find(TIME_UNITS, (unit) => _.isNumber(_.get(config?.past, unit)));
		if (validTimeUnit) {
			pastDate = today.subtract(_.get(config?.past, validTimeUnit) || 0, validTimeUnit);
		}
	}

	if (config?.future) {
		const validTimeUnit = _.find(TIME_UNITS, (unit) => _.isNumber(_.get(config?.future, unit)));
		if (validTimeUnit) {
			futureDate = today.add(_.get(config?.future, validTimeUnit) || 0, validTimeUnit);
		}
		if (config?.future?.is_end_of_month) {
			futureDate = (futureDate ?? today).endOf('month');
		}
		if (config?.future?.is_end_of_year) {
			futureDate = (futureDate ?? today).endOf('year');
		}
	}

	return { pastDate, futureDate };
};

const applyDateOverrides = (
	key: string,
	formValues: EligibilityRequestForm.IFormModel,
	dates: EligibilityRequestForm.IPastFutureDate,
): EligibilityRequestForm.IPastFutureDate => {
	let { pastDate, futureDate } = dates;

	if (key === schemaKeys.serviceStartDate && formValues?.serviceEndDate) {
		futureDate = dateParser(formValues.serviceEndDate);
	}
	if (key === schemaKeys.serviceEndDate && formValues?.serviceStartDate) {
		pastDate = dateParser(formValues.serviceStartDate);
	}

	return { pastDate, futureDate };
};

const calculatePastFutureDates = (
	config: Payers.IFieldConfig,
	formValues: EligibilityRequestForm.IFormModel,
	key: string,
): EligibilityRequestForm.IPastFutureDate => {
	const configDates = calculateDatesFromConfig(config);
	return applyDateOverrides(key, formValues, configDates);
};

/**
 * function to disable service dates
 */
const disableServiceDates = (currentDate: IDate, dates: EligibilityRequestForm.IPastFutureDate): boolean => {
	const { pastDate, futureDate } = dates;
	if (pastDate && futureDate) {
		return currentDate?.isBefore(pastDate) || currentDate?.isAfter(futureDate);
	} else if (pastDate) {
		return currentDate?.isBefore(pastDate);
	} else if (futureDate) {
		return currentDate?.isAfter(futureDate);
	}
	// If both pastDate and futureDate are undefined, don't disable any dates
	return false;
};

// tooltip for service dates
const serviceDatesTooltip = ({ pastDate, futureDate }: EligibilityRequestForm.IPastFutureDate): string => {
	const parsedPastDate = pastDate ? pastDate.subtract(1, 'days').format(MONTH_DATE_YEAR_SLASH_FORMAT) : null;
	const parsedFutureDate = futureDate ? futureDate.add(1, 'days').format(MONTH_DATE_YEAR_SLASH_FORMAT) : null;
	if (parsedPastDate && parsedFutureDate) {
		return `Select a date between ${parsedPastDate} to ${parsedFutureDate}`;
	} else if (parsedPastDate) {
		return `Select a date after ${parsedPastDate}`;
	} else if (parsedFutureDate) {
		return `Select a date before ${parsedFutureDate}`;
	}
	return '';
};

// sort service types
const sortedServiceTypes = (serviceTypeOptions: IMultiSelectOption[]): IMultiSelectOption[] => {
	return _.sortBy(serviceTypeOptions, (service) => {
		const codeAsNumber = parseFloat(service?.value as string);
		return [
			service?.disabled ? 1 : 0,
			isNaN(codeAsNumber) ? 1 : 0,
			isNaN(codeAsNumber) ? service.value : codeAsNumber.toString().padStart(4, '0'),
		];
	});
};

// prepare member id soft validation message
const prepareMemberIdValidationMessage = (memberIdLength: number, minLength?: number, maxLength?: number): string => {
	const hasMinLength = _.isNumber(minLength);
	const hasMaxLength = _.isNumber(maxLength);

	const memberIdLengthMessage = ` But the Member ID entered is ${memberIdLength} characters.`;
	if (hasMinLength && hasMaxLength) {
		if (memberIdLength < minLength || memberIdLength > maxLength) {
			if (minLength === maxLength) {
				return `The Member ID should be ${minLength} characters.${memberIdLengthMessage}`;
			}
			return `The Member ID should be ${minLength} to ${maxLength} characters.${memberIdLengthMessage}`;
		}
	} else if (hasMinLength && memberIdLength < minLength) {
		return `The Member ID should be at least ${minLength} characters.${memberIdLengthMessage}`;
	} else if (hasMaxLength && memberIdLength > maxLength) {
		return `The Member ID should not exceed ${maxLength} characters.${memberIdLengthMessage}`;
	}
	return '';
};

const prepareMaintenanceValidationMessage = (
	transactionType: Payers.ITransactionType,
	payerMaintenance?: Payers.IPayerMaintenance[],
): EligibilityRequestForm.IValidationMessage | null => {
	const maintenanceGroups = groupTransactionMaintenance(payerMaintenance || []);
	const maintenanceInfo = maintenanceGroups[transactionType];

	if (!maintenanceInfo?.isDown) {
		return null;
	}

	return {
		text: 'This payer is currently undergoing scheduled maintenance, and may not be responsive at this time.',
		markdown: maintenanceInfo.markdown,
	};
};

// prepare eligibility fields soft validation messages
const prepareFieldValidationMessages = ({
	formValues,
	payerData,
	payerMaintenance,
}: {
	formValues: EligibilityRequestForm.IFormModel;
	payerData?: Payers.IPayerMeta;
	payerMaintenance?: Payers.IPayerMaintenance[];
}): EligibilityRequestForm.IValidationMessage[] => {
	const validationMessages = [] as EligibilityRequestForm.IValidationMessage[];
	// Payer maintenance validation
	const maintenanceMessage = prepareMaintenanceValidationMessage(TRANSACTION_TYPE_CODES.eligibility, payerMaintenance);
	if (maintenanceMessage) {
		validationMessages.push(maintenanceMessage);
	}
	if (!payerData) {
		return validationMessages;
	} else {
		const eligibilityData = payerData?.payerMeta?.eligibilityMeta;
		const memberIdConfig = _.get(eligibilityData?.fieldConfig, fieldsConfigMapping.memberId);
		// member ID validation
		if (memberIdConfig) {
			const memberIdLength = _.size(formValues?.memberId);
			if (memberIdLength > 0) {
				const minLength = memberIdConfig?.min_length;
				const maxLength = memberIdConfig?.max_length;
				const memberIdValidationMessage = prepareMemberIdValidationMessage(memberIdLength, minLength, maxLength);
				if (memberIdValidationMessage) {
					validationMessages.push({ text: memberIdValidationMessage });
				}
			}
		}
	}
	return validationMessages;
};

export {
	ADD_FORM_TYPE,
	CLAIM_FORM_TYPE,
	ACTIVE_PAYERS_CODES,
	ENROLLMENT_REQUIRED_CODES,
	PAYER_ENABLED_CODES,
	EDIT_FORM_TYPE,
	COVERAGE_FORM_TYPE,
	PATIENT_FORM_TYPE,
	prepareEligibilityFormInitialModel,
	prepareEligibilitySubmitModel,
	convertToUpperCase,
	disableFutureDates,
	prepareEligibilityResubmitModel,
	getSelectedPatientCombination,
	defaultServiceType,
	isValidPersistCombination,
	isValidProvider,
	getInitialPayer,
	disableServiceDates,
	calculateDatesFromConfig,
	calculatePastFutureDates,
	serviceDatesTooltip,
	sortedServiceTypes,
	prepareFieldValidationMessages,
	prepareMemberIdValidationMessage,
	prepareMaintenanceValidationMessage,
	prepareProviderIdentifierInitialModel,
	getSelectedProviderCombination,
	getProviderIdentifiersById,
	getFirstSelectedCombination,
};
